import { useEffect, useRef, useState } from 'react';
import 'katex/dist/katex.min.css';
import { ResponsiveFractalCanvas } from './ResponsiveFractalCanvas';
import { ViewportFractalCanvas } from './ViewportFractalCanvas';
import {
  DEFAULT_NEWTON_C,
  allSubstructureSections,
  complexRelaxationPresets,
  polynomialFamilyPresets,
  smaleBasinPresets,
} from './fractalPresets';
import { EXPLORER_PALETTE } from './fractalPalette';
import { codeSnippets, equations } from './staticContent';
import { renderedCode, renderedEquations } from './staticContent.generated';

const substructureSources = [...new Map(
  allSubstructureSections
    .filter(({ source, sourceLabel }) => source && sourceLabel)
    .map(({ source, sourceLabel }) => [source, sourceLabel])
)];

export const PAGE_SECTIONS = [
  { id: 'intro', label: 'Introduction' },
  { id: 'world-overview', label: 'World overview' },
  { id: 'equation', label: 'Fractal equation' },
  {
    id: 'substructures',
    label: 'Sub-structures',
    subsections: [
      { id: 'substructures-polynomial-families', label: 'Polynomial families' },
      { id: 'substructures-complex-relaxation', label: 'Complex relaxation' },
      { id: 'substructures-smale-basins', label: 'Smale basins' },
    ],
  },
  { id: 'coloring', label: 'Fractal coloring' },
  { id: 'about', label: 'About' },
];

// KaTeX and highlight.js run in scripts/generate-static-content.mjs, so the
// article ships as pre-rendered HTML and neither library reaches the browser.
const Equation = ({ name }) => {
  const html = renderedEquations[name];
  if (html === undefined) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`Missing pre-rendered equation "${name}". Run \`yarn generate:content\`.`);
    }
    return <div className="equation" data-nosnippet="">{equations[name] ?? name}</div>;
  }
  return <div className="equation" data-nosnippet="" dangerouslySetInnerHTML={{ __html: html }} />;
};

const CodeBlock = ({ name }) => {
  const html = renderedCode[name];
  if (html === undefined) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`Missing pre-rendered code snippet "${name}". Run \`yarn generate:content\`.`);
    }
    return <pre><code className="language-javascript hljs">{codeSnippets[name] ?? name}</code></pre>;
  }
  return (
    <pre>
      <code
        className="language-javascript hljs"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </pre>
  );
};

const FractalSubsection = ({ entry, preferredRenderer }) => {
  const { section, headingId, title, description, preset } = entry;
  const isDeepCameraPreset = Boolean(preset.camera);

  return (
    <section
      id={section}
      className="subsection fractal-subsection"
      data-section={section}
      aria-labelledby={headingId}
    >
      <h4 id={headingId}>{title}</h4>
      <p>{description}</p>
      <ResponsiveFractalCanvas
        {...preset}
        deferUntilVisible={isDeepCameraPreset}
        preferredRenderer={preferredRenderer}
      />
    </section>
  );
};

const SidebarNav = ({ headerRef }) => {
  const [activeSection, setActiveSection] = useState(PAGE_SECTIONS[0].id);
  const [sidebarTop, setSidebarTop] = useState(null);
  const navRef = useRef(null);

  useEffect(() => {
    let scrollFrame = null;
    const selectSection = nextId => setActiveSection(prev => (prev === nextId ? prev : nextId));

    const updateSidebarState = () => {
      scrollFrame = null;
      if (document.body?.classList.contains('fullscreen-active')) return;

      const bannerCandidates = [
        headerRef?.current,
        document.querySelector('[data-section="intro"] .responsive-fractal'),
      ].filter(Boolean);
      let bannerBottom = null;
      for (const el of bannerCandidates) {
        const rect = el.getBoundingClientRect();
        if (rect.top !== 0 || rect.bottom !== 0 || rect.height !== 0) {
          bannerBottom = bannerBottom === null ? rect.bottom : Math.max(bannerBottom, rect.bottom);
        }
      }
      if (bannerBottom !== null) {
        let nextTop = Math.max(32, Math.round(bannerBottom + 17));
        const heroEl = document.querySelector('[data-section="hero-canvas"]');
        const navRect = navRef.current?.getBoundingClientRect?.();
        if (heroEl && navRect && navRect.height > 0) {
          const heroRect = heroEl.getBoundingClientRect();
          if (heroRect.top !== 0 || heroRect.bottom !== 0 || heroRect.height !== 0) {
            nextTop = Math.min(nextTop, Math.round(heroRect.top - navRect.height - 24));
          }
        }
        setSidebarTop(prev => (prev === nextTop ? prev : nextTop));
      }

      const rects = PAGE_SECTIONS.map(section => {
        const el = document.getElementById(section.id);
        return el ? { section, rect: el.getBoundingClientRect() } : null;
      }).filter(Boolean);

      if (rects.length === 0) return;

      const hasLayout = rects.some(({ rect }) =>
        rect.top !== 0 || rect.bottom !== 0 || rect.height !== 0
      );
      if (!hasLayout) return;

      const viewportHeight = window.innerHeight || 800;
      const docHeight = document.documentElement?.scrollHeight || 0;
      if (window.scrollY > 0 && docHeight > viewportHeight &&
          window.scrollY + viewportHeight >= docHeight - 24) {
        selectSection(PAGE_SECTIONS[PAGE_SECTIONS.length - 1].id);
        return;
      }

      const threshold = Math.max(120, viewportHeight * 0.28);
      let currentSection = PAGE_SECTIONS[0];
      for (const { section, rect } of rects) {
        if (rect.top <= threshold) {
          currentSection = section;
        }
      }

      if (currentSection.subsections?.length) {
        let currentSubId = currentSection.subsections[0].id;
        for (const sub of currentSection.subsections) {
          const subEl = document.getElementById(sub.id);
          if (!subEl) continue;
          const subRect = subEl.getBoundingClientRect();
          const subHasLayout = subRect.top !== 0 || subRect.bottom !== 0 || subRect.height !== 0;
          if (subHasLayout && subRect.top <= threshold) {
            currentSubId = sub.id;
          }
        }
        selectSection(currentSubId);
        return;
      }

      selectSection(currentSection.id);
    };

    const handleScrollOrResize = () => {
      if (globalThis.IS_REACT_ACT_ENVIRONMENT || typeof requestAnimationFrame !== 'function') {
        updateSidebarState();
        return;
      }
      if (scrollFrame === null) {
        scrollFrame = requestAnimationFrame(updateSidebarState);
      }
    };

    updateSidebarState();
    window.addEventListener('scroll', handleScrollOrResize, { passive: true });
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      if (scrollFrame !== null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(scrollFrame);
      }
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [headerRef]);

  return (
    <nav
      ref={navRef}
      className="sidebar-nav"
      aria-label="Page sections"
      style={sidebarTop !== null ? { transform: `translate3d(0, ${sidebarTop}px, 0)` } : undefined}
    >
      <ul className="sidebar-nav-list">
        {PAGE_SECTIONS.map(({ id, label, subsections }) => {
          const hasActiveChild = Boolean(subsections?.some(sub => sub.id === activeSection));
          const isActive = activeSection === id;
          return (
            <li key={id} className="sidebar-nav-item">
              <a
                href={`#${id}`}
                className={`sidebar-nav-link${isActive ? ' active' : ''}${hasActiveChild ? ' active-parent' : ''}`}
                aria-current={isActive ? 'location' : undefined}
                onClick={() => setActiveSection(subsections?.length ? subsections[0].id : id)}
              >
                {label}
              </a>
              {subsections?.length > 0 && (
                <ul className="sidebar-nav-sublist">
                  {subsections.map(sub => {
                    const isSubActive = activeSection === sub.id;
                    return (
                      <li key={sub.id} className="sidebar-nav-subitem">
                        <a
                          href={`#${sub.id}`}
                          className={`sidebar-nav-link sidebar-nav-sublink${isSubActive ? ' active' : ''}`}
                          aria-current={isSubActive ? 'location' : undefined}
                          onClick={() => setActiveSection(sub.id)}
                        >
                          {sub.label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

const NEWTON_HERO_PALETTE = { ...EXPLORER_PALETTE, phaseOffset: 0.12, juliaC: DEFAULT_NEWTON_C };
const NEWTON_SPIRAL_C = Object.freeze([1.0, 0.32]);
const NEWTON_SPIRAL_PALETTE = { ...EXPLORER_PALETTE, phaseOffset: 0.25, juliaC: NEWTON_SPIRAL_C };
const ITERATION_PALETTE = { ...EXPLORER_PALETTE, phaseOffset: 0.12, useMu: false, juliaC: DEFAULT_NEWTON_C };

const App = () => {
  const preferredRenderer = 'webgl';
  const headerRef = useRef(null);

  return <>
  <header ref={headerRef}>
    <h1>The Newton Fractal</h1>
  </header>

  <SidebarNav headerRef={headerRef} />

  <section id="intro" className="section" data-section="intro">
  <ResponsiveFractalCanvas
    width={1600}
    height={1600}
    fullWidth
    xRange={[-1.8, 1.8]}
    yRange={[-1.8, 1.8]}
    juliaC={DEFAULT_NEWTON_C}
    palette={NEWTON_HERO_PALETTE}
    preferredRenderer={preferredRenderer}
  />
  <div className="container">
    <p>
      If we apply Newton&apos;s root-finding method to a polynomial in the complex
      plane and color each starting point by which root it converges to and how
      quickly it arrives, the boundary between the root basins unfolds into an
      intricate fractal known as the Newton fractal.
    </p>
    <Equation name="newton-with-i" />
  </div>
  </section>

  <section id="world-overview" className="section" data-section="world-overview" aria-labelledby="world-overview-heading">
  <div className="container">
    <h2 id="world-overview-heading">World overview</h2>
    <p>
      For the cubic polynomial p(z) = z&sup3; &minus; 1, the three cube roots of
      unity sit on the unit circle at 120&deg; intervals: r0 = 1 on the positive
      real axis and r1, r2 = &minus;1/2 &plusmn; i&radic;3/2 in the left
      half-plane.
    </p>
    <ResponsiveFractalCanvas
      width={800}
      height={800}
      xRange={[-2.0, 2.0]}
      yRange={[-2.0, 2.0]}
      juliaC={DEFAULT_NEWTON_C}
      palette={NEWTON_HERO_PALETTE}
      preferredRenderer={preferredRenderer}
    />

    <p>
      In 1879, Arthur Cayley asked how Newton&apos;s method partitions the complex
      plane among the three cube roots of unity. While each root is surrounded by
      a broad immediate basin of attraction, the boundary separating any two
      basins is a Lakes-of-Wada fractal: every boundary point touches all three
      root basins simultaneously.
    </p>
    <p>
      At the origin z = 0 where the derivative p&prime;(z) = 3z&sup2; vanishes,
      Newton&apos;s map has a pole of order 2 that slings nearby points toward
      infinity and wraps all three basins into six-fold symmetric chains of
      rosettes. Adding a complex relaxation factor a = 1 + 0.32i twists these
      radial necklaces into logarithmic spirals.
    </p>
    <ResponsiveFractalCanvas
      width={800}
      height={600}
      xRange={[-1.6, 1.6]}
      yRange={[-1.2, 1.2]}
      juliaC={NEWTON_SPIRAL_C}
      palette={NEWTON_SPIRAL_PALETTE}
      preferredRenderer={preferredRenderer}
    />
  </div>
  </section>

  <section id="equation" className="section" data-section="equation" aria-labelledby="equation-heading">
  <div className="container">
    <h2 id="equation-heading">Newton fractal equation</h2>

    <p>The generalized Newton-Raphson iteration for a complex polynomial p(z) is:</p>
    <Equation name="newton-general-iteration" />

    <p>For the canonical cubic polynomial and its derivative:</p>
    <Equation name="newton-cubic-polynomial" />

    <p>Its three attracting fixed points are the cube roots of unity:</p>
    <Equation name="newton-cube-roots" />

    <p>Each pixel supplies the initial complex guess z0:</p>
    <Equation name="complex-number" />

    <p>While the complex relaxation parameter a (default a = 1) controls the step size and angle:</p>
    <Equation name="complex-parameter" />

    <p>Expanding the rational map (2z&sup3; + 1) / (3z&sup2;) for a = 1 reduces each step to real and imaginary components:</p>
    <Equation name="real-part-iteration" />
    <Equation name="imaginary-part-iteration" />

    <p>
      For general cubic polynomials such as Stephen Smale&apos;s counterexample
      p(z) = z&sup3; &minus; 2z + 2, the inflection point z0 = 0 is a free
      critical point that lands in a superattracting period-2 cycle instead of a
      root:
    </p>
    <Equation name="newton-smale-cubic" />

    <section>
      <p>
        We explore the Newton fractal by seeding each pixel&apos;s starting (x0, y0)
        coordinates into the recurrence and recording how the orbit behaves:
      </p>
      <ul>
        <li>whether the orbit converges within tolerance &epsilon; of one of the polynomial&apos;s roots (the Fatou root basins) or remains trapped in a non-convergent cycle or on the repelling Julia set boundary</li>
        <li>if the series converges to a root, we make note of:
          <ul>
            <li>which root basin k captured the trajectory</li>
            <li>the number of iterations and residual distance to the root upon convergence</li>
          </ul>
        </li>
      </ul>
    </section>
  </div>
  </section>

  <section id="substructures" className="section" data-section="substructures" aria-labelledby="substructures-heading">
  <div className="container">
    <h2 id="substructures-heading">Sub-structures</h2>
    <p>
      Unlike the Mandelbrot set, whose boundary folds around a critical point to
      reveal new structures at arbitrarily deep magnifications, a fixed Newton
      map is uniformly expanding on its Julia boundary and linearizes into
      self-similar copies of its poles and periodic cycles. The richest
      geometric transformations emerge when we vary the polynomial family,
      twist the step with complex relaxation, or introduce free critical points
      that capture orbits into non-convergent Smale basins.
    </p>

    <div
      id="substructures-polynomial-families"
      className="substructures-subsection"
      data-section="polynomial-families"
    >
      <h3 className="substructures-subsection-title">Polynomial families</h3>
      <p className="substructures-subsection-intro">
        Changing the polynomial p(z) reshapes both the attracting root basins
        and the poles where p&prime;(z) = 0. A pole of order m wraps all root
        basins m + 1 times around the singularity into a 2(m + 1)-rayed
        starburst, while adding a root at the origin splits the central pole
        into a ring of simple poles where four basins braid together.
      </p>
      {polynomialFamilyPresets.map(entry => (
        <FractalSubsection key={entry.section} entry={entry} preferredRenderer={preferredRenderer} />
      ))}
    </div>
  </div>

  <div
    id="substructures-complex-relaxation"
    className="substructures-subsection substructures-subsection-deep"
    data-section="complex-relaxation"
  >
    <div className="container">
      <h3 className="substructures-subsection-title">Complex relaxation</h3>
      <p className="substructures-subsection-intro">
        Scaling the Newton step by a complex relaxation factor a = a_x + ia_y
        shifts each simple root&apos;s multiplier from 0 (quadratic
        superattraction) to 1 &minus; a (linear attraction). Inside the
        stability disk |1 &minus; a| &lt; 1, complex and over-relaxed steps
        twist radial pole channels into logarithmic spirals, pinwheels, and
        Celtic knots.
      </p>
      {complexRelaxationPresets.map(entry => (
        <FractalSubsection key={entry.section} entry={entry} preferredRenderer={preferredRenderer} />
      ))}
    </div>
  </div>

  <div
    id="substructures-smale-basins"
    className="substructures-subsection substructures-subsection-labyrinthine"
    data-section="smale-basins"
  >
    <div className="container">
      <h3 className="substructures-subsection-title">Non-convergent Smale basins</h3>
      <p className="substructures-subsection-intro">
        Stephen Smale (1985) and Curt McMullen (1987) showed that Newton&apos;s
        method is not globally convergent for cubic polynomials. Because the
        Newton derivative N&prime;(z) = p(z)p&Prime;(z)/p&prime;(z)&sup2;
        vanishes at the inflection point where p&Prime;(z) = 0, that free
        critical point can fall into a superattracting periodic cycle, carving
        ink-black non-convergent islands directly between the colored root
        basins.
      </p>
      {smaleBasinPresets.map(entry => (
        <FractalSubsection key={entry.section} entry={entry} preferredRenderer={preferredRenderer} />
      ))}
    </div>
  </div>
  </section>

  <section id="coloring" className="section" data-section="coloring" aria-labelledby="coloring-heading">
  <div className="container">
    <h2 id="coloring-heading">Fractal coloring</h2>

    <p>
      Root convergence time: given a starting point (x0, y0), we can color each
      pixel by identifying which root r0, r1, or r2 the orbit converges to, the
      number of iterations required to enter a small &epsilon;-neighborhood of
      that root, and the residual distance upon arrival.
    </p>
    <CodeBlock name="iterate-until-escape" />
  </div>
  </section>

  <section className="section" data-section="implementation">
  <div className="container">
    <p>
      We&apos;ll set the RGBA colors of every pixel in the image based on the
      converged root index and the number of iterations. Shifting the palette
      phase by one-third of a cycle per root assigns each basin its own distinct
      color family.
    </p>
    <CodeBlock name="iteration-color-palette" />

    <p>
      Here&apos;s a general function for drawing Newton fractals onto canvas
      elements. Changing the polynomial recurrence or relaxation factor a
      transforms the root basins and spiral geometry.
    </p>

    <CodeBlock name="draw-fractal-canvas" />

    <p>
      Here&apos;s the cubic Newton fractal (z&sup3; &minus; 1 = 0) colored by
      root basin and integer iteration count alone, showing discrete contour
      bands around each root and along the six rays meeting at the origin.
    </p>
    <ResponsiveFractalCanvas
      width={1600}
      height={1600}
      xRange={[-1.6, 1.6]}
      yRange={[-1.6, 1.6]}
      juliaC={DEFAULT_NEWTON_C}
      palette={ITERATION_PALETTE}
      preferredRenderer={preferredRenderer}
    />
    <p>
      Because simple roots are superattracting fixed points where the error
      squares on every step (|z_n &minus; r_k| &asymp; |z_&#123;n-1&#125; &minus; r_k|&sup2;),
      taking a double logarithm of the residual root distance produces a
      continuous iteration count (&mu;) that eliminates contour banding.
    </p>
    <CodeBlock name="escape-time-coloring" />
    <p>
      Here&apos;s the same Newton fractal with continuous root-convergence counts
      and a smooth Oklab palette. Each of the three cube roots of unity receives
      its own 120&deg; Lakes of Wada phase sector in persimmon coral, celadon
      jade, and mineral indigo, while root-centered smoothingSteps remove
      contour seams.
    </p>
    <ResponsiveFractalCanvas
      width={1600}
      height={1600}
      xRange={[-1.6, 1.6]}
      yRange={[-1.6, 1.6]}
      juliaC={DEFAULT_NEWTON_C}
      palette={NEWTON_HERO_PALETTE}
      preferredRenderer={preferredRenderer}
    />
    <p>
      Damped and Nova variants of Newton&apos;s method introduce a complex
      relaxation parameter a or an additive shift +c at each step, bridging the
      basins of attraction of root-finding maps with the parameter-plane islands
      of the Mandelbrot and Julia sets.
    </p>
  </div>
  </section>

    <section id="about" className="section" data-section="about" aria-labelledby="about-heading">
      <div className="container">
        <h2 id="about-heading">About</h2>
        <p>
          All fractals on the page are interactive and rendered when they&apos;re in view.
          The renderer uses WebGL by default for high performance, falling back to canvas if WebGL is not available.
        </p>
        <p>Fractal image controls when hovering</p>
        <ul>
          <li>shift + scroll mouse wheel — zoom in/out</li>
          <li>double-click (or shift + double-click) — zoom in/out at cursor</li>
          <li>+ / &minus; keys — zoom in/out</li>
          <li>pinch — zoom in/out</li>
          <li>click and drag — pan the view</li>
          <li>two-finger swipe — pan the view on touchscreens</li>
          <li>one-finger swipe — scroll the page</li>
          <li>cmd-z — undo the last camera action</li>
          <li>0 — reset view to its initial position</li>
        </ul>

        <p>TODO</p>
        <ul>
          <li>Plot higher-degree polynomials (z&#8308; &minus; 1, z&#8309; &minus; 1) and Nova parameter planes</li>
          <li>Improve coloring code for better dynamic range</li>
          <li>Enable customizing colors</li>
          <li>Enable resetting each canvas to its original state</li>
          <li>Allow editing x and y ranges: text inputs or click/drag slider</li>
          <li>Maybe show the (x0, y0) center of each canvas</li>
          <li>Draw boxes when selecting parts of the canvas to zoom in on</li>
        </ul>
      </div>
    </section>

    <footer>
      <div className="container">
        <h2 id="references-heading">References</h2>
        <ul>
          {substructureSources.map(([source, sourceLabel]) => (
            <li key={source}><a href={source}>{sourceLabel}</a></li>
          ))}
        </ul>
      </div>
    </footer>

    <ViewportFractalCanvas preferredRenderer={preferredRenderer} />
</>;
};

export default App;
